import React, { useState } from 'react';
import { Upload } from 'lucide-react';

const AdminBookCover = () => {
  const [bookCoverUrl, setBookCoverUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleUrlUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/book-cover/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ book_cover_url: bookCoverUrl }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Book cover updated successfully! Refresh the homepage to see changes.');
        setBookCoverUrl('');
      } else {
        setError(data.detail || 'Failed to update book cover');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Book cover update error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F1E8', padding: '80px 40px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '3rem',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#111111',
            textAlign: 'center'
          }}
        >
          Update Book Cover
        </h1>
        <p
          style={{
            fontSize: '1.1rem',
            color: '#666666',
            textAlign: 'center',
            marginBottom: '60px'
          }}
        >
          Upload your book cover image or provide an image URL
        </p>

        {/* URL Update Form */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            marginBottom: '40px'
          }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.8rem',
              fontWeight: '600',
              marginBottom: '24px',
              color: '#111111'
            }}
          >
            Update Book Cover URL
          </h2>

          {message && (
            <div
              style={{
                padding: '16px',
                backgroundColor: '#e6f7e6',
                border: '1px solid #00cc00',
                borderRadius: '4px',
                color: '#006600',
                marginBottom: '24px'
              }}
            >
              {message}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '16px',
                backgroundColor: '#ffe6e6',
                border: '1px solid #ff0000',
                borderRadius: '4px',
                color: '#cc0000',
                marginBottom: '24px'
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleUrlUpdate}>
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#111111'
                }}
              >
                Image URL
              </label>
              <input
                type="url"
                value={bookCoverUrl}
                onChange={(e) => setBookCoverUrl(e.target.value)}
                placeholder="https://example.com/book-cover.jpg"
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '1rem',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF',
                  color: '#111111'
                }}
              />
              <p
                style={{
                  fontSize: '0.85rem',
                  color: '#666666',
                  marginTop: '8px'
                }}
              >
                Paste the URL of your book cover image (must be publicly accessible)
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                width: '100%',
                backgroundColor: loading ? '#999999' : '',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Upload size={20} />
              {loading ? 'Updating...' : 'Update Book Cover'}
            </button>
          </form>
        </div>

        {/* Instructions */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            padding: '32px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}
        >
          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#111111'
            }}
          >
            How to Upload Your Book Cover
          </h3>
          <ol
            style={{
              fontSize: '1rem',
              color: '#333333',
              lineHeight: '1.8',
              paddingLeft: '20px'
            }}
          >
            <li style={{ marginBottom: '12px' }}>
              Upload your book cover image to a service like Imgur, ImgBB, or your own hosting
            </li>
            <li style={{ marginBottom: '12px' }}>
              Copy the direct image URL (must end with .jpg, .png, or .webp)
            </li>
            <li style={{ marginBottom: '12px' }}>
              Paste the URL in the form above and click "Update Book Cover"
            </li>
            <li>
              Your book cover will appear on the homepage and buy page immediately
            </li>
          </ol>
          <p
            style={{
              fontSize: '0.95rem',
              color: '#666666',
              marginTop: '20px',
              fontStyle: 'italic'
            }}
          >
            <strong>Recommended size:</strong> 400x550 pixels for best display quality
          </p>
        </div>

        {/* Back to Home */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a
            href="/"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              backgroundColor: 'transparent',
              color: '#111111',
              border: '2px solid #111111',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#111111';
              e.target.style.color = '#F4F1E8';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#111111';
            }}
          >
            ← Back to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminBookCover;
